import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { api } from '../services/api/client.js';
import { freePublishingState } from '../lib/freePublishing.js';
import { freePreviewClientEnabled } from '../lib/flags.js';
import { freeOnboardingAvailable, freeOnboardingDestination } from '../lib/freeOnboarding.js';

export function useFreeOnboardingStart(enabled) {
  const { user } = useAuth();
  const dash = useDashboardData();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const live = useRef(null);
  live.current = { userId: user?.id, enabled };
  const mounted = useRef(false);
  const pending = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { pending.current = null; setBusy(false); setError(false); }, [user?.id]);
  const entitlements = dash.entitlementsError ? null : dash.entitlements;
  const available = Boolean(enabled && freePreviewClientEnabled() &&
    (freeOnboardingAvailable(entitlements) || freeOnboardingDestination(entitlements)));

  const start = async () => {
    if (!available || pending.current || !user?.id) return;
    const owner = user.id;
    const request = {};
    const isCurrent = () => mounted.current && live.current.userId === owner && live.current.enabled && freePreviewClientEnabled();
    pending.current = request; setBusy(true); setError(false);
    try {
      if (!freeOnboardingDestination(entitlements)) {
        const response = await api.auth.startFreePreview();
        if (!isCurrent()) return;
        // Auth acknowledges enrollment; fresh billing below still owns access.
        if (!['granted', 'pending_verification'].includes(response?.status)) {
          throw new Error('Setup enrollment was not granted');
        }
      }
      const fresh = await dash.invalidateResource('entitlements', { fresh: true, throwOnError: true });
      if (!isCurrent()) return;
      const destination = freePublishingState(fresh) && freeOnboardingDestination(fresh);
      if (!destination) throw new Error('No granted setup');
      navigate(destination);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (pending.current === request) pending.current = null;
      if (mounted.current && live.current.userId === owner) setBusy(false);
    }
  };
  return { available, start, busy, error };
}
